/**
 * Prism geometry for the Work Atlas - pure functions, no DOM.
 *
 * A column stands on its facility's map point. Its footprint is an isometric
 * diamond (half-width A, half-depth B, screen px) centred on that point; it
 * rises straight up the screen. Three faces are drawn: left, right and top.
 *
 *   Height is LINEAR in count with ONE scale for every facility:
 *     height(px) = count x k,   k = min(MAX_PX_PER_RECORD, MAX_COLUMN_PX / maxTotal)
 *   where maxTotal is the largest all-category total of any facility on the
 *   map. Nothing is capped or clipped: the scale is chosen so the tallest
 *   column is MAX_COLUMN_PX, and the scale bar states k. Hiding a category in
 *   the legend removes its segment but never changes k (a fixed cohort scale,
 *   so a toggle cannot fake growth), and zooming the map never changes k.
 *
 * Screen y grows downward, so "up" is negative y.
 */
import type { AtlasDomain, DomainCounts } from './atlasApi';

/** Footprint half-width and half-depth in px (B/A ~ 0.57, close to a 30-degree isometric). */
export const PRISM_A = 7;
export const PRISM_B = 4;
/** The tallest column, in px. */
export const MAX_COLUMN_PX = 160;
/** A small cohort must not turn 3 records into a tower: at most this many px per record. */
export const MAX_PX_PER_RECORD = 4;

/** px per open record - one number for the whole map. */
export function pxPerRecord(maxTotal: number): number {
  if (!(maxTotal > 0)) return MAX_PX_PER_RECORD;
  return Math.min(MAX_PX_PER_RECORD, MAX_COLUMN_PX / maxTotal);
}

/**
 * A scale-bar length in whole records, 1/2/5 x 10^n, whose drawn height is
 * near 40px. The bar is drawn at exactly records x k px.
 */
export function scaleBar(k: number): { records: number; px: number } {
  const raw = 40 / k;
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  const candidates = [1, 2, 5, 10].map((m) => m * pow);
  let best = candidates[0];
  for (const c of candidates) if (Math.abs(c * k - 40) < Math.abs(best * k - 40)) best = c;
  const records = Math.max(1, best);
  return { records, px: records * k };
}

export interface Segment { key: AtlasDomain; count: number; from: number; to: number }

/** Stacked segments, bottom to top in `order`, for the shown categories only. Heights in px from the base. */
export function stackSegments(open: DomainCounts, shown: AtlasDomain[], order: AtlasDomain[], k: number): Segment[] {
  const out: Segment[] = [];
  let h = 0;
  for (const key of order) {
    if (!shown.includes(key)) continue;
    const count = open[key] || 0;
    if (count <= 0) continue;
    const to = h + count * k;
    out.push({ key, count, from: h, to });
    h = to;
  }
  return out;
}

const pts = (p: Array<[number, number]>) => p.map(([x, y]) => `${round(x)},${round(y)}`).join(' ');
const round = (v: number) => Math.round(v * 100) / 100;

/** The footprint diamond at height h (h = 0 is the floor). */
export function diamond(h: number, a = PRISM_A, b = PRISM_B): string {
  return pts([[-a, -h], [0, b - h], [a, -h], [0, -b - h]]);
}

/** Left and right side faces of the slab between heights h0 and h1. */
export function sideFaces(h0: number, h1: number, a = PRISM_A, b = PRISM_B): { left: string; right: string } {
  return {
    left: pts([[-a, -h0], [0, b - h0], [0, b - h1], [-a, -h1]]),
    right: pts([[0, b - h0], [a, -h0], [a, -h1], [0, b - h1]]),
  };
}

/** The outline of a whole column of height h (for the selection ring). */
export function silhouette(h: number, a = PRISM_A, b = PRISM_B): string {
  return pts([[-a, 0], [0, b], [a, 0], [a, -h], [0, -b - h], [-a, -h]]);
}

/** Painter's order: farther (smaller screen y) first, so nearer columns draw over them. */
export function backToFront<T extends { x: number; y: number }>(points: T[]): T[] {
  return [...points].sort((p, q) => p.y - q.y || p.x - q.x);
}

export const shownTotal = (open: DomainCounts, shown: AtlasDomain[]) =>
  shown.reduce((s, d) => s + (open[d] || 0), 0);
